import { waitForWeglotApi } from "@lyle/weglot";

const LANGUAGE_CODE_PATTERN = /^[a-z]{2}(?:-[a-z]{2})?$/i;

class LyleWeglotSwitcher extends HTMLElement {
  #abortController = null;
  #weglot = null;
  #onLanguageChanged = null;
  #onDocumentPointerDown = null;
  #onDocumentKeyDown = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#unbindWeglot();
    this.#unbindDocument();
  }

  async #init() {
    const weglot = await waitForWeglotApi();

    if (!weglot || !this.isConnected) {
      return;
    }

    this.#weglot = weglot;
    this.#renderLanguages();
    this.#bindTrigger();
    this.#bindWeglot();
    this.hidden = this.#languages().length < 2;
  }

  #languages() {
    const options = this.#weglot?.options;

    if (!options) {
      return [];
    }

    const destinationLanguages = (options.languages || [])
      .map((language) => language.language_to)
      .filter(Boolean);

    return [...new Set([options.language_from, ...destinationLanguages])].filter(
      (code) => LANGUAGE_CODE_PATTERN.test(code),
    );
  }

  #renderLanguages() {
    const list = this.querySelector("[data-weglot-list]");
    const trigger = this.querySelector("[data-weglot-trigger]");

    if (!list || !trigger) {
      return;
    }

    const currentLanguage = this.#currentLanguage();
    const listLabel = this.dataset.listLabel || "";

    list.replaceChildren();
    list.setAttribute("aria-label", listLabel);

    for (const code of this.#languages()) {
      const item = document.createElement("li");
      const option = document.createElement("button");
      const normalisedCode = code.toLowerCase();

      option.type = "button";
      option.className = "weglot-switcher__option text-body-sm";
      option.dataset.weglotLanguage = normalisedCode;
      option.textContent = normalisedCode.toUpperCase();
      option.setAttribute("role", "option");
      option.setAttribute(
        "aria-selected",
        normalisedCode === currentLanguage ? "true" : "false",
      );

      option.addEventListener(
        "click",
        () => {
          this.#selectLanguage(normalisedCode);
        },
        { signal: this.#abortController.signal },
      );

      item.append(option);
      list.append(item);
    }

    this.#syncLabel(currentLanguage);
  }

  #bindTrigger() {
    const trigger = this.querySelector("[data-weglot-trigger]");

    trigger?.addEventListener(
      "click",
      () => {
        this.#toggleList();
      },
      { signal: this.#abortController.signal },
    );
  }

  #bindWeglot() {
    this.#unbindWeglot();

    if (!this.#weglot?.on) {
      return;
    }

    this.#onLanguageChanged = (language) => {
      this.#syncLabel(language);
      this.#closeList();
    };

    this.#weglot.on("languageChanged", this.#onLanguageChanged);
    this.#weglot.on("switchersReady", () => {
      this.#renderLanguages();
      this.hidden = this.#languages().length < 2;
    });
  }

  #unbindWeglot() {
    if (!this.#weglot?.off || !this.#onLanguageChanged) {
      return;
    }

    this.#weglot.off("languageChanged", this.#onLanguageChanged);
    this.#onLanguageChanged = null;
  }

  #bindDocument() {
    if (this.#onDocumentPointerDown) {
      return;
    }

    this.#onDocumentPointerDown = (event) => {
      if (!this.contains(event.target)) {
        this.#closeList();
      }
    };

    this.#onDocumentKeyDown = (event) => {
      if (event.key === "Escape") {
        this.#closeList();
        this.querySelector("[data-weglot-trigger]")?.focus();
      }
    };

    document.addEventListener("pointerdown", this.#onDocumentPointerDown);
    document.addEventListener("keydown", this.#onDocumentKeyDown);
  }

  #unbindDocument() {
    if (this.#onDocumentPointerDown) {
      document.removeEventListener("pointerdown", this.#onDocumentPointerDown);
      this.#onDocumentPointerDown = null;
    }

    if (this.#onDocumentKeyDown) {
      document.removeEventListener("keydown", this.#onDocumentKeyDown);
      this.#onDocumentKeyDown = null;
    }
  }

  #toggleList() {
    const list = this.querySelector("[data-weglot-list]");

    if (!list) {
      return;
    }

    if (list.hidden) {
      this.#openList();
      return;
    }

    this.#closeList();
  }

  #openList() {
    const list = this.querySelector("[data-weglot-list]");
    const trigger = this.querySelector("[data-weglot-trigger]");

    if (!list || !trigger) {
      return;
    }

    list.hidden = false;
    trigger.setAttribute("aria-expanded", "true");
    this.classList.add("is-open");
    this.#bindDocument();
  }

  #closeList() {
    const list = this.querySelector("[data-weglot-list]");
    const trigger = this.querySelector("[data-weglot-trigger]");

    if (!list || !trigger) {
      return;
    }

    list.hidden = true;
    trigger.setAttribute("aria-expanded", "false");
    this.classList.remove("is-open");
    this.#unbindDocument();
  }

  #selectLanguage(code) {
    const normalisedCode = code.toLowerCase();

    if (!this.#weglot?.switchTo || normalisedCode === this.#currentLanguage()) {
      this.#closeList();
      return;
    }

    this.#weglot.switchTo(normalisedCode);
    this.#syncLabel(normalisedCode);
    this.#closeList();
  }

  #currentLanguage() {
    const language =
      this.#weglot?.getCurrentLang?.() ||
      document.documentElement.lang ||
      this.#weglot?.options?.language_from ||
      "en";

    return language.toLowerCase().split("-")[0];
  }

  #syncLabel(language) {
    const code = (language || this.#currentLanguage())
      .toLowerCase()
      .split("-")[0];
    const label = this.querySelector("[data-weglot-label]");
    const trigger = this.querySelector("[data-weglot-trigger]");
    const openLabelTemplate = this.dataset.openLabel || "";

    if (label) {
      label.textContent = code.toUpperCase();
    }

    if (trigger && openLabelTemplate) {
      trigger.setAttribute(
        "aria-label",
        openLabelTemplate.replaceAll("__LANG__", code.toUpperCase()),
      );
    }

    this.querySelectorAll("[data-weglot-language]").forEach((option) => {
      option.setAttribute(
        "aria-selected",
        option.dataset.weglotLanguage === code ? "true" : "false",
      );
    });
  }
}

if (!customElements.get("lyle-weglot-switcher")) {
  customElements.define("lyle-weglot-switcher", LyleWeglotSwitcher);
}
